import { ChevronDown, Search, X } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { cx } from './cx'
import { useFieldControl } from './Field'

/** Shared look of every text-like control. Exported for the rare custom control (e.g. a combobox). */
export const controlClass =
  // 16 px on touch: iOS Safari zooms into any focused field below that and stays zoomed, so the page scrolls sideways.
  'w-full min-w-0 rounded-control border border-line-strong bg-surface text-body pointer-coarse:text-base text-ink tabular ' +
  'placeholder:text-ink-3 transition-[border-color,box-shadow] duration-150 ' +
  'hover:border-ink-3/60 ' +
  // The ring replaces the global outline so the focus state hugs the control's radius.
  'focus-visible:border-accent focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-accent/20 ' +
  'aria-invalid:border-negative aria-invalid:focus-visible:ring-negative/20 ' +
  'disabled:cursor-not-allowed disabled:bg-bg-subtle disabled:text-ink-3'

// Not in controlClass: a <select> always matches :read-only.
const readOnly = 'read-only:bg-bg-subtle'

const height = 'h-11 pointer-fine:h-10'

type InputProps = Omit<ComponentProps<'input'>, 'className' | 'size'> & {
  /** Decorative icon or short text inside the left edge (e.g. "€"). */
  leading?: ReactNode
  /** Decorative content inside the right edge (e.g. a unit "km"). */
  trailing?: ReactNode
  className?: string
}

export function Input({ leading, trailing, className, ...props }: InputProps) {
  const p = useFieldControl(props)
  const input = (
    <input
      {...p}
      className={cx(controlClass, readOnly, height, leading ? 'pl-9' : 'pl-3', trailing ? 'pr-10' : 'pr-3', !leading && !trailing && className)}
    />
  )
  if (!leading && !trailing) return input
  return (
    <div className={cx('relative flex items-center', className)}>
      {leading && (
        <span className="pointer-events-none absolute left-3 flex text-ink-3 [&_svg]:size-4" aria-hidden="true">
          {leading}
        </span>
      )}
      {input}
      {trailing && (
        <span className="pointer-events-none absolute right-3 flex text-small text-ink-3" aria-hidden="true">
          {trailing}
        </span>
      )}
    </div>
  )
}

export function Textarea({ className, rows = 4, ...props }: Omit<ComponentProps<'textarea'>, 'className'> & { className?: string }) {
  const p = useFieldControl(props)
  return <textarea rows={rows} {...p} className={cx(controlClass, readOnly, 'field-sizing-content min-h-24 px-3 py-2.5', className)} />
}

type SelectProps = Omit<ComponentProps<'select'>, 'className'> & {
  /** Shown as a disabled first option when the value is empty. */
  placeholder?: string
  className?: string
}

export function Select({ placeholder, children, className, ...props }: SelectProps) {
  const p = useFieldControl(props)
  return (
    <div className={cx('relative flex items-center', className)}>
      <select {...p} className={cx(controlClass, height, 'cursor-pointer appearance-none pl-3 pr-9')}>
        {placeholder !== undefined && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {children}
      </select>
      <ChevronDown aria-hidden="true" className="pointer-events-none absolute right-3 size-4 text-ink-3" />
    </div>
  )
}

type NativePickerProps = Omit<ComponentProps<'input'>, 'className' | 'type' | 'size'> & { className?: string }

/** Native date picker; value is `YYYY-MM-DD` (local date, no time zone shift). */
export function DateField({ className, ...props }: NativePickerProps) {
  const p = useFieldControl(props)
  return <input type="date" {...p} className={cx(controlClass, height, 'px-3', className)} />
}

/** Native time picker; value is `HH:mm` local time. */
export function TimeField({ className, step = 300, ...props }: NativePickerProps) {
  const p = useFieldControl(props)
  return <input type="time" step={step} {...p} className={cx(controlClass, height, 'px-3', className)} />
}

type SearchInputProps = Omit<ComponentProps<'input'>, 'className' | 'type' | 'value' | 'onChange' | 'size'> & {
  value: string
  onValueChange: (value: string) => void
  /** Accessible name when no Field label wraps it. */
  label?: string
  className?: string
}

export function SearchInput({ value, onValueChange, label = 'Suchen', placeholder = 'Suchen …', className, ...props }: SearchInputProps) {
  const p = useFieldControl(props)
  return (
    <search className={cx('relative flex items-center', className)}>
      <Search aria-hidden="true" className="pointer-events-none absolute left-3 size-4 text-ink-3" />
      <input
        type="search"
        aria-label={p.id ? undefined : label}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        {...p}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && value) {
            e.stopPropagation()
            onValueChange('')
          }
          props.onKeyDown?.(e)
        }}
        className={cx(controlClass, height, 'pl-9 pr-10 [&::-webkit-search-cancel-button]:hidden')}
      />
      {value && (
        <button
          type="button"
          aria-label="Suche leeren"
          onClick={() => onValueChange('')}
          className="absolute right-1 grid size-9 place-items-center rounded-[6px] text-ink-3 hover:text-ink pointer-fine:size-8"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      )}
    </search>
  )
}
